import { FC } from 'react';
import { CheckCircle2 } from 'lucide-react';

import Modal from '@new/common/ui/Modal/Modal';
import { Button } from '@new/common/ui/Button/Button';
import { useAddTranscript, type AddTranscriptMethod } from '../../hooks/useAddTranscript';
import { FileUploadStep } from './FileUploadStep';
import { ExtractionLoader } from './ExtractionLoader';
import { TranscriptReviewEditStep } from './TranscriptReviewEditStep';

interface AddTranscriptModalProps {
  isOpen: boolean;
  method: AddTranscriptMethod;
  onClose: () => void;
  onAdded?: () => void;
}

const STEP_TITLES: Record<string, string> = {
  upload: 'Upload your transcript',
  generating: 'Reading your transcript',
  review: 'Add your transcript',
  success: 'Transcript added',
};

export const AddTranscriptModal: FC<AddTranscriptModalProps> = ({
  isOpen,
  method,
  onClose,
  onAdded,
}) => {
  const add = useAddTranscript({ onDone: onAdded, initialMethod: method });

  const close = () => {
    add.reset();
    onClose();
  };

  const renderStep = () => {
    switch (add.step) {
      case 'upload':
        return <FileUploadStep isUploading={add.isUploading} onSubmit={add.submitFile} />;
      case 'generating':
        return <ExtractionLoader />;
      case 'review':
        return <TranscriptReviewEditStep add={add} />;
      case 'success':
        return (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <CheckCircle2 className="size-10 text-(--primary-O900)" />
            <p className="[font-size:var(--fs-p2)]! font-semibold text-(--neutral-black-NB1000)!">
              Your transcript was added.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  const showFooter = add.step === 'review' || add.step === 'success';

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      dismissable={false}
      title={STEP_TITLES[add.step]}
      contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
      className="h-[85vh] max-h-[85vh] max-w-5xl! md:max-w-5xl! lg:max-w-5xl! **:data-[slot=select]:h-12! **:data-[slot=select]:rounded-xl! **:data-[slot=select]:border-[1.5px]! **:data-[slot=select]:border-solid! **:data-[slot=select]:border-[#E1E7EA]! **:data-[slot=select]:bg-[#FCFDFD]! [&_input[data-slot=input]]:h-12! [&_input[data-slot=input]]:md:h-12! [&_input[data-slot=input]]:xl:h-12! [&_input[data-slot=input]]:rounded-xl! [&_input[data-slot=input]]:border-[1.5px]! [&_input[data-slot=input]]:border-solid! [&_input[data-slot=input]]:border-[#E1E7EA]! [&_input[data-slot=input]]:bg-[#FCFDFD]!"
    >
      <div className="flex h-full flex-col gap-6 overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">{renderStep()}</div>

        {showFooter ? (
          <div className="flex shrink-0 items-center justify-between border-t border-(--neutral-black-NB100) pt-4">
            {add.step === 'review' ? (
              <>
                {add.canGoBack ? (
                  <Button variant="neutral" onClick={add.goBack} text="Back" />
                ) : (
                  <Button variant="neutral" onClick={close} text="Cancel" />
                )}
                <Button
                  variant="primary"
                  disabled={!add.canSave || add.isSaving}
                  onClick={add.save}
                  text={add.isSaving ? 'Saving…' : 'Save'}
                />
              </>
            ) : (
              <div className="ml-auto">
                <Button variant="primary" onClick={close} text="Done" />
              </div>
            )}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};
